import { useEffect, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const pad = (n) => String(n).padStart(2, "0");
export const isoToDmy = (iso) => (iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : "");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isoToDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : undefined);

function dmyToIso(s) {
  const m = s.trim().match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  if (d.getDate() !== +m[1] || d.getMonth() !== +m[2] - 1) return null;
  return toIso(d);
}

const box = "flex items-center rounded-md border border-input bg-white focus-within:ring-2 focus-within:ring-blue-600/30";

export default function DatePicker({ value, onChange, max, disabled, testid, className = "", clearable, placeholder = "DD-MM-YYYY" }) {
  const [text, setText] = useState(isoToDmy(value));
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setText(isoToDmy(value));
    setErr(false);
  }, [value]);

  const commit = () => {
    if (!text.trim()) {
      if (clearable) onChange("");
      else setText(isoToDmy(value));
      return;
    }
    const iso = dmyToIso(text);
    if (!iso || (max && iso > max)) { setErr(true); return; }
    setErr(false);
    if (iso !== value) onChange(iso);
    else setText(isoToDmy(iso));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={`${box} ${err ? "border-red-500" : ""} ${disabled ? "opacity-50" : ""} ${className}`}>
        <input
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          inputMode="numeric"
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
          className="flex-1 min-w-0 bg-transparent px-3 h-full py-2 text-sm money outline-none"
          data-testid={testid}
        />
        {clearable && value && !disabled && (
          <button type="button" onClick={() => onChange("")} className="px-1 text-slate-400 hover:text-slate-700" data-testid={`${testid}-clear`}><X className="h-3.5 w-3.5" /></button>
        )}
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} className="px-2.5 text-slate-500 hover:text-blue-800 transition-colors" data-testid={`${testid}-calendar-btn`}>
            <CalendarDays className="h-4 w-4" />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={isoToDate(value)}
          defaultMonth={isoToDate(value)}
          onSelect={(d) => { if (d) onChange(toIso(d)); setOpen(false); }}
          disabled={max ? { after: isoToDate(max) } : undefined}
          weekStartsOn={1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function MonthPicker({ value, onChange, testid, className = "" }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(value ? +value.slice(0, 4) : new Date().getFullYear());
  useEffect(() => {
    if (value) setYear(+value.slice(0, 4));
  }, [value]);
  const label = value ? `${MONTHS[+value.slice(5, 7) - 1]} ${value.slice(0, 4)}` : "Select month";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${box} h-10 px-3 justify-between text-sm w-full ${className}`} data-testid={testid}>
          <span className="money">{label}</span><CalendarDays className="h-4 w-4 text-slate-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="flex items-center justify-between mb-2">
          <button type="button" onClick={() => setYear(year - 1)} className="p-1 rounded hover:bg-slate-100" data-testid={`${testid}-prev-year`}><ChevronLeft className="h-4 w-4" /></button>
          <span className="font-medium text-sm">{year}</span>
          <button type="button" onClick={() => setYear(year + 1)} className="p-1 rounded hover:bg-slate-100" data-testid={`${testid}-next-year`}><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {MONTHS.map((m, i) => {
            const v = `${year}-${pad(i + 1)}`;
            return (
              <button key={m} type="button" onClick={() => { onChange(v); setOpen(false); }} data-testid={`${testid}-option-${v}`}
                className={`rounded-md py-2 text-sm transition-colors ${v === value ? "bg-blue-800 text-white" : "hover:bg-slate-100"}`}>{m}</button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
